import React, { useEffect } from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Box,
  Typography,
  TextField,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';

import {
  useCreateOrUpdateFeeScheduleMutation,
  useDeleteFeeScheduleMutation,
} from '../../../../common/apis/AdminApi';
import SnackbarUtils from '../../../../common/utils/SnackbarUtilsConfigurator';
import { FeeSchedule } from '../../../types/FeeSchedule';

import { useFeeScheduleFile } from './useFeeScheduleFile';

type Props = {
  onClose: () => void;
  feeSchedule?: FeeSchedule;
};

const FeeSchedulePopup: React.FC<Props> = ({ onClose, feeSchedule }) => {
  const [updateOrSaveFeeSchedule, { isLoading }] =
    useCreateOrUpdateFeeScheduleMutation();

  const [deleteFeeSchedule, { isLoading: isDeleting }] =
    useDeleteFeeScheduleMutation();

  const [practiceId, setPracticeId] = React.useState<string>('');
  const [practiceName, setPracticeName] = React.useState<string>('');
  const [codes, setCodes] = React.useState<string[]>([]);

  const [openDialog, setOpenDialog] = React.useState(false);

  const {
    error,
    fileName,
    extractedCodes,
    getRootProps,
    getInputProps,
    reset,
  } = useFeeScheduleFile(false);

  useEffect(() => {
    if (feeSchedule) {
      setPracticeId(feeSchedule.practiceId);
      setPracticeName(feeSchedule.practiceName);
      setCodes(feeSchedule.codes);
    } else {
      setPracticeId('');
      setPracticeName('');
      setCodes([]);
    }
  }, [feeSchedule]);

  useEffect(() => {
    if (extractedCodes.length > 0) {
      setCodes(extractedCodes);
    }
  }, [extractedCodes]);

  const submit = async () => {
    const data: Omit<FeeSchedule, 'createdAt' | 'updatedAt'> = {
      id: feeSchedule ? feeSchedule.id : 0,
      practiceId: practiceId,
      practiceName: practiceName,
      codes: codes,
    };

    updateOrSaveFeeSchedule(data)
      .unwrap()
      .then((res) => {
        SnackbarUtils.success(res.message);
        closePopup();
      });
  };

  const deleteOne = async () => {
    if (!feeSchedule) return;
    deleteFeeSchedule({
      id: feeSchedule.id.toString(),
    })
      .unwrap()
      .then((res) => {
        SnackbarUtils.success(res.message);
        closePopup();
      });
  };

  const closePopup = () => {
    reset();
    onClose();
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelete = async () => {
    handleCloseDialog();
    await deleteOne();
  };

  return (
    <Box
      sx={{
        maxWidth: '456px',
        minHeight: '240px',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flex: 1,
        borderRadius: '15px',
        padding: '40px',
        flexDirection: 'column',
      }}
    >
      <Box className='row' sx={{ alignItems: 'center' }}>
        <Typography
          sx={{
            alignSelf: 'center',
            whiteSpace: 'break-spaces',
            textAlign: 'center',
            ml: 'auto',
            mr: 'auto',
          }}
          color='text.primary'
          variant='h5'
        >
          {feeSchedule ? 'Edit' : 'Create'} Fee Schedule
        </Typography>
      </Box>
      <Box
        sx={{
          marginTop: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        <TextField
          label='Practice ID *'
          size='small'
          color='secondary'
          sx={{
            width: '100%',
          }}
          InputProps={{
            style: {
              minHeight: 40,
              borderBlockColor: 'primary',
              backgroundColor: 'white',
              borderRadius: '40px',
            },
          }}
          value={practiceId}
          onChange={(e) => setPracticeId(e.target.value)}
        />
        <TextField
          label='Practice Name'
          size='small'
          color='secondary'
          sx={{
            width: '100%',
          }}
          InputProps={{
            style: {
              minHeight: 40,
              borderBlockColor: 'primary',
              backgroundColor: 'white',
              borderRadius: '40px',
            },
          }}
          value={practiceName}
          onChange={(e) => setPracticeName(e.target.value)}
        />
        <Box>
          <Typography
            color='text.primary'
            variant='subtitle2'
            textAlign='center'
            sx={{ marginBottom: '8px' }}
          >
            The document must be an Excel file with a CPT column containing the
            codes.
          </Typography>
          <Stack spacing={2.5}>
            <Box
              {...getRootProps()}
              sx={{
                display: 'flex',
                backgroundColor: 'rgba(255, 255, 255, 0.20)',
                minHeight: '80px',
                flexDirection: 'row',
                flex: 1,
                borderRadius: '24px',
                border: '2px dashed #ccc',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '20px',
                flexGrow: 1,
              }}
            >
              <Box>
                <Box
                  sx={{
                    flexDirection: 'row',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography color='secondary'>
                    Drag and drop fee schedule file
                  </Typography>
                  <Button variant='text'>
                    <input {...getInputProps()} />
                    <Typography color='primary'>or browse files</Typography>
                  </Button>
                </Box>
                <Typography
                  color='text.secondary'
                  variant='subtitle2'
                  textAlign='center'
                >
                  (xls, xlsx)
                </Typography>
                <Typography
                  color='text.primary'
                  variant='subtitle2'
                  textAlign='center'
                  sx={{ marginTop: '8px' }}
                >
                  {fileName}
                </Typography>
              </Box>
            </Box>
            {error && (
              <Typography sx={{ textAlign: 'center' }} color='error'>
                {error}
              </Typography>
            )}
          </Stack>
          <Typography
            color='text.secondary'
            variant='subtitle2'
            textAlign='center'
            sx={{ marginTop: '8px' }}
          >
            {feeSchedule && `This Fee Schedule contains ${codes.length} codes.`}
          </Typography>
          <Typography
            color='text.secondary'
            variant='subtitle2'
            textAlign='center'
            sx={{ marginTop: '8px' }}
          >
            {extractedCodes.length > 0 &&
              `Extracted ${extractedCodes.length} codes from the file.`}
          </Typography>
        </Box>
      </Box>
      {feeSchedule && (
        <>
          <LoadingButton
            onClick={handleOpenDialog}
            disabled={isLoading || isDeleting}
            loading={isDeleting}
            disableElevation
            color='error'
            variant='contained'
            sx={{
              borderRadius: '166px',
              width: '180px',
              height: '40px',
              marginTop: '16px',
            }}
          >
            <Typography color='white'>Delete Fee Schedule</Typography>
          </LoadingButton>
          <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            aria-labelledby='alert-dialog-title'
            aria-describedby='alert-dialog-description'
          >
            <DialogTitle id='alert-dialog-title'>
              {'Delete Fee Schedule'}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id='alert-dialog-description'>
                Are you sure you want to delete this fee schedule?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} color='primary'>
                Cancel
              </Button>
              <Button onClick={handleDelete} color='primary' autoFocus>
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      <Box
        sx={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          alignSelf: 'center',
          display: 'flex',
          marginTop: '16px',
        }}
      >
        <Button
          onClick={closePopup}
          disabled={isLoading || isDeleting}
          disableElevation
          color='appGray2'
          variant='contained'
          sx={{
            borderRadius: '50px',
            width: '180px',
            height: '60px',
            marginRight: '15px',
          }}
        >
          <Typography color='white'>Cancel</Typography>
        </Button>
        <LoadingButton
          onClick={submit}
          disabled={isLoading || !practiceId || error !== '' || isDeleting}
          loading={isLoading}
          disableElevation
          color='primary'
          variant='contained'
          sx={{
            borderRadius: '50px',
            width: '180px',
            height: '60px',
          }}
        >
          <Typography color='white'>Save</Typography>
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default FeeSchedulePopup;
